import { Ruler } from 'features/fde/rulers/rulerTypes'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'

/**
 * Is an assessment region REMOTE from a structural discontinuity?
 *
 * API 579-1 Part 5 Eq. 5.10: a region qualifies when its distance to the
 * nearest major structural discontinuity clears `1.8 * sqrt(D * t_c)`. The
 * distance itself is what the auto-generated rulers measure — one per (LTA,
 * nearby asset-model feature) pair, closest approach from the LTA's flaw
 * boundary to the feature.
 *
 * Pure and React-free, mirroring `classifyFlawShape`/`deriveFlawShape`: the
 * rule is a predicate over numbers, and the plumbing that produces those
 * numbers is the caller's problem.
 *
 * WHAT THIS IS NOT: the rulers measure distance to the nearest MODELLED asset
 * feature, which includes seam welds, plates and pipe wraps — not only major
 * structural discontinuities. That UNDERSTATES the true distance, so the answer
 * errs toward "not remote". The nearest feature's title is returned so the
 * engineer can see when the blocker is only a weld and override it.
 */

export const MSD_THRESHOLD_FACTOR = 1.8

/**
 * A measurement, NOT a verdict. There is deliberately no `remote` /
 * `not-remote` field: the rulers cannot establish remoteness from a major
 * structural discontinuity (see the blind spots above), so the only thing that
 * compares these two numbers is the warning that consumes them — in exactly one
 * place, `describeDiscontinuityProximity`.
 */
export interface DerivedRemoteness {
  /** Distance to the nearest modelled feature, mm. */
  lmsdMm: number | null
  /** Name of that nearest feature, for the readout. */
  nearestFeatureTitle: string | null
  /** `1.8 * sqrt(D * t_c)`, mm. */
  thresholdMm: number | null
}

const UNDETERMINED: DerivedRemoteness = {
  lmsdMm: null,
  nearestFeatureTitle: null,
  thresholdMm: null,
}

/**
 * The straight-line length of a ruler, in mm.
 *
 * Pure geometry, and deliberately identical to the Part-5 flaw-size version it
 * replaces — including that it does NOT honour `wraps`. Wrapping is a POLICY
 * concern handled by `deriveRemoteness` (which drops such rulers rather than
 * mis-measuring them), so that Part 5's long-standing behaviour is unchanged by
 * the move.
 */
export function rulerLengthMm(ruler: Ruler): number {
  const dx = ruler.selection.x.size.toNumber(WORKING_UNIT_THICKNESS)
  const dy = ruler.selection.y.size.toNumber(WORKING_UNIT_THICKNESS)
  return Math.hypot(dx, dy)
}

/**
 * The Eq. 5.10 limit, `1.8 * sqrt(D * t_c)`, in mm. `null` when either input is
 * missing or non-positive.
 */
export function msdThresholdMm({
  dMm,
  tcMm,
}: {
  dMm: number | null | undefined
  tcMm: number | null | undefined
}): number | null {
  if (
    dMm === null ||
    dMm === undefined ||
    tcMm === null ||
    tcMm === undefined ||
    !Number.isFinite(dMm) ||
    !Number.isFinite(tcMm) ||
    dMm <= 0 ||
    tcMm <= 0
  ) {
    return null
  }
  return MSD_THRESHOLD_FACTOR * Math.sqrt(dMm * tcMm)
}

/**
 * Resolve one LTA's remoteness from its rulers.
 *
 * ZERO RULERS IS UNDETERMINED, NOT REMOTE. It is tempting to read "no feature
 * within the backend's search radius" as the strongest possible evidence of
 * remoteness, and sometimes it is — but ruler entries carry no provenance, so
 * that case is indistinguishable from "ruler generation never ran for this
 * LTA". Guessing wrong here would hand out Type A, so we decline to guess.
 * Making the common (genuinely remote) case work needs a generation marker from
 * `ffs-service`.
 *
 * Rulers that WRAP the component seam are dropped: `rulerLengthMm` measures the
 * raw endpoint delta, whereas a wrapping ruler's true span is
 * `cdfExtent - delta` (see `computeDimensions` in `features/fde/rulers/rulerMarkup.ts`).
 * `ffs-service` never emits `wraps`, so this is unreachable today — it exists so
 * that a hand-drawn ruler, if one is ever indication-tagged, is skipped rather
 * than silently mis-measured.
 */
export function deriveRemoteness({
  rulers,
  dMm,
  tcMm,
}: {
  rulers: ReadonlyArray<Ruler>
  dMm: number | null | undefined
  tcMm: number | null | undefined
}): DerivedRemoteness {
  const usable = rulers.filter((r) => !r.wraps?.length)
  if (usable.length === 0) return UNDETERMINED

  let nearest = usable[0]
  let lmsdMm = rulerLengthMm(nearest)
  for (const ruler of usable.slice(1)) {
    const length = rulerLengthMm(ruler)
    if (length < lmsdMm) {
      lmsdMm = length
      nearest = ruler
    }
  }
  if (!Number.isFinite(lmsdMm)) return UNDETERMINED

  return {
    lmsdMm,
    nearestFeatureTitle: nearest.title || null,
    thresholdMm: msdThresholdMm({ dMm, tcMm }),
  }
}
